import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Search, X, Tag as TagIcon } from "lucide-react";

const humanDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const tag = searchParams.get("tag") || "";
  const search = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasFilter = Boolean(category || tag || search);

  useEffect(() => {
    document.title = "Blog — Architectural Knowledge Center | Dream Home Engineers";
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (tag) params.tag = tag;
    if (search) params.search = search;
    // Safety timeout — loading must always clear within 10s so users
    // never see a permanent "Loading…" state if one of the three API
    // calls hangs on a cold ingress or transient network hiccup.
    const safetyTimer = setTimeout(() => setLoading(false), 10000);
    Promise.all([
      api.get("/blog/posts", { params }),
      api.get("/blog/categories").catch(() => ({ data: [] })),
      api.get("/blog/popular", { params: { limit: 5 } }).catch(() => ({ data: [] })),
    ]).then(([r1, r2, r3]) => {
      const list = r1.data?.items || [];
      // If viewing "all", surface the first featured post as the hero.
      const feat = (!category && !tag && !search) ? list.find((p) => p.featured) || list[0] : null;
      setFeatured(feat || null);
      setItems(feat ? list.filter((p) => p.id !== feat.id) : list);
      setCategories(r2.data || []);
      setPopular(r3.data || []);
    }).catch(() => {
      // Never leave the user staring at a spinner — collapse to empty state.
      setFeatured(null);
      setItems([]);
    }).finally(() => {
      clearTimeout(safetyTimer);
      setLoading(false);
    });
    return () => clearTimeout(safetyTimer);
  }, [category, tag, search]);

  const clearFilters = () => setSearchParams({});

  const currentFilterLabel = useMemo(() => {
    if (category) return `Category: ${category}`;
    if (tag) return `Tag: #${tag}`;
    if (search) return `Search: "${search}"`;
    return "";
  }, [category, tag, search]);

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="max-w-3xl">
          <div className="overline text-accent">The Journal</div>
          <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95] tracking-tighter">
            Architectural knowledge center.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Design guides, buyer advice, BIM & CAD deep-dives, and construction insights from our architects.
          </p>
        </div>

        {/* Search */}
        <div className="mt-8 flex gap-2 max-w-xl">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="blog-public-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearchParams(searchInput ? { search: searchInput } : {})}
              placeholder="Search articles…"
              className="rounded-none pl-9 h-11"
            />
          </div>
          <Button onClick={() => setSearchParams(searchInput ? { search: searchInput } : {})} className="rounded-none h-11">Search</Button>
        </div>

        {currentFilterLabel && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 border border-border">
            <span className="text-sm">{currentFilterLabel}</span>
            <button data-testid="clear-blog-filters" onClick={clearFilters} className="text-muted-foreground hover:text-rose-600"><X size={12} /></button>
          </div>
        )}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {loading && <div className="text-muted-foreground">Loading articles…</div>}

          {!loading && featured && (
            <Link to={`/blog/${featured.slug}`} data-testid="blog-featured-post" className="block border border-border group plan-card-hover mb-10">
              {featured.cover_image_url && (
                <div className="aspect-[16/9] overflow-hidden bg-muted">
                  <img src={featured.cover_image_url} alt={featured.title} className="w-full h-full object-cover plan-card-img" loading="eager" />
                </div>
              )}
              <div className="p-8">
                <div className="flex items-center gap-3">
                  <span className="overline text-accent">{featured.category}</span>
                  {featured.reading_time_minutes ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} />{featured.reading_time_minutes} min</span> : null}
                </div>
                <h2 className="font-display text-3xl lg:text-4xl mt-3 group-hover:text-accent transition-fast">{featured.title}</h2>
                {featured.excerpt && <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>}
                <div className="mt-6 text-xs text-muted-foreground">{featured.author_name || "Dream Home Engineers"} · {humanDate(featured.published_at || featured.created_at)}</div>
              </div>
            </Link>
          )}

          {!loading && items.length === 0 && !featured && (
            hasFilter ? (
              <div className="border border-dashed border-border p-16 text-center" data-testid="blog-empty-filtered">
                <div className="overline text-accent">No articles found</div>
                <p className="font-display text-2xl mt-3">Try a different filter or search term.</p>
                <button
                  data-testid="blog-empty-clear-filter"
                  onClick={clearFilters}
                  className="mt-6 overline text-accent hover:underline"
                >
                  Clear filters →
                </button>
              </div>
            ) : (
              <div className="border border-dashed border-border p-16 text-center" data-testid="blog-empty-catalog">
                <div className="overline text-accent">Coming soon</div>
                <p className="font-display text-2xl mt-3 max-w-md mx-auto leading-snug">
                  No articles published yet.
                </p>
                <p className="mt-4 text-muted-foreground max-w-md mx-auto">
                  We're preparing architectural guides, buyer advice, and BIM/CAD deep-dives from our design team. Please check back soon.
                </p>
                <Link
                  to="/plans"
                  data-testid="blog-empty-browse-plans"
                  className="mt-8 inline-flex items-center gap-2 overline text-accent hover:underline"
                >
                  Browse house plans in the meantime <ArrowRight size={12} />
                </Link>
              </div>
            )
          )}

          {items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-post-grid">
              {items.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group border border-border block plan-card-hover">
                  {p.cover_image_url && (
                    <div className="aspect-[16/9] overflow-hidden bg-muted">
                      <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover plan-card-img" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-3">
                      <span className="overline text-accent">{p.category}</span>
                      {p.reading_time_minutes ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} />{p.reading_time_minutes} min</span> : null}
                    </div>
                    <h3 className="font-display text-xl mt-3 leading-tight group-hover:text-accent transition-fast">{p.title}</h3>
                    {p.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>}
                    <div className="mt-4 text-xs text-muted-foreground">{humanDate(p.published_at || p.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-8">
          {categories.length > 0 && (
            <div className="border border-border p-6">
              <div className="overline text-accent">Categories</div>
              <ul className="mt-3 space-y-1">
                {categories.map((c) => (
                  <li key={c.name}>
                    <Link
                      to={`/blog?category=${encodeURIComponent(c.name)}`}
                      data-testid={`blog-category-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`flex items-center justify-between py-1 text-sm hover:text-accent ${c.name === category ? "text-accent font-semibold" : ""}`}
                    >
                      <span>{c.name}</span>
                      <span className="text-muted-foreground text-xs font-mono">{c.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {popular.length > 0 && (
            <div className="border border-border p-6">
              <div className="overline text-accent">Popular posts</div>
              <ul className="mt-3 space-y-3" data-testid="blog-popular-list">
                {popular.map((p, i) => (
                  <li key={p.id}>
                    <Link to={`/blog/${p.slug}`} className="flex items-start gap-3 group">
                      <span className="text-2xl font-display text-accent flex-shrink-0 leading-none">{i + 1}</span>
                      <div>
                        <div className="text-sm font-semibold group-hover:text-accent transition">{p.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">{humanDate(p.published_at || p.created_at)}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>
    </Layout>
  );
};

export default Blog;
